import { MixpanelEvents } from "@/constants/mixpanel-events";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { useEffect, useRef } from "react";

export type PurchasePlacement =
  | "trial_offer"
  | "expired_trial"
  | "upgrade"
  | "subscription_settings";

// Telemetry must never change the outcome of a purchase.
function emit(event: string, properties: Record<string, string | number | boolean>) {
  try {
    trackMixpanelEvent(event, properties);
  } catch {
    // Keep the purchase flow available when telemetry fails.
  }
}

export function usePaywallViewed(placement: PurchasePlacement, visible = true) {
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) {
      previous.current = null;
      return;
    }
    if (previous.current === placement) return;
    previous.current = placement;
    emit(MixpanelEvents.PAYWALL_VIEWED, { placement, paywall_variant: placement });
  }, [placement, visible]);
}

export function createPurchaseAttempt(pkg: PurchasesPackage, placement: PurchasePlacement) {
  const properties = {
    purchase_attempt_id: crypto.randomUUID(),
    placement,
    package_id: pkg.identifier,
    product_id: pkg.product.identifier,
  };
  let unlocked = false;
  emit(MixpanelEvents.PURCHASE_STARTED, properties);
  return {
    result(result: "succeeded" | "cancelled" | "failed") {
      emit(MixpanelEvents.PURCHASE_RESULT, {
        ...properties,
        result,
        ...(result === "failed" ? { failure_category: "store_purchase" } : {}),
      });
    },
    verification(result: "active" | "inactive" | "failed") {
      emit(MixpanelEvents.SUBSCRIPTION_VERIFICATION_RESULT, {
        ...properties,
        result,
        ...(result === "failed" ? { failure_category: "backend_verification" } : {}),
      });
      if (result === "active" && !unlocked) {
        unlocked = true;
        emit(MixpanelEvents.SUBSCRIPTION_ACCESS_UNLOCKED, properties);
      }
    },
  };
}
